import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Card,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Link,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import {enqueueSnackbar} from 'notistack';
import {useToolbar} from '../../components/ToolbarContext';
import {CenteredCircularSpinner} from '../../shared/components/CenteredCircularSpinner';
import {logError} from '../../shared/services/logger.service';
import {SavaquanHttpError} from '../../shared/utils/http-error';
import {CompanyPlanChargeDto, CompanyPlanDto} from '../../shared/dtos/company-plan.dto';
import {
  addEstimates,
  changePlan,
  getCompanyPlan,
  openBillingPortal,
  startCheckout,
} from '../../shared/services/company-plan.service';
import {
  allowance,
  annualFor,
  BillingCycle,
  count,
  planMoney,
  planPrice,
  priceFor,
  rateMoney,
  topTier,
} from '../../shared/pricing/plan-pricing';
import {describeChange, firstBigTotal, isLocked, PlanPick, pickPrice, renewsOn, toChoice} from './plan-choice';

/*
 * SQ-415: the owner's plan page, the web twin of Android's Account plan
 * section and Choose your plan (PlanScreen.kt). Plan and usage come from the
 * server's mirror of Stripe; the card is only ever typed on Stripe's pages
 * (Checkout for the first plan, the portal for the card, receipts and
 * cancelling), and coming back from either re-reads the plan from Stripe.
 */

const GREEN = '#2e7d32';
const GREEN_SOFT = '#e8f5e9';
const GREEN_EDGE = '#a5d6a7';
const AMBER = '#ed6c02';
const HAIR = '#e0e0e0';

const message = (e: unknown, fallback: string) => (e instanceof SavaquanHttpError && e.message) ? e.message : fallback;

export const PlanPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const toolbarContext = useToolbar();
  const chooserRef = useRef<HTMLDivElement | null>(null);

  const [data, setData] = useState<CompanyPlanDto | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState(false);
  const [busy, setBusy] = useState(false);
  const [choosing, setChoosing] = useState(false);
  const [addOpen, setAddOpen] = useState(false);

  useEffect(() => {
    toolbarContext.initToolbar('PlanPage', 'Plan & billing');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = useCallback((refresh: boolean) => {
    setLoadError(null);
    return getCompanyPlan(navigate, refresh)
      .then(setData)
      .catch(e => {
        if (e instanceof SavaquanHttpError && e.statusCode === 403) {
          setForbidden(true);
          return;
        }
        logError('Error loading the plan', {}, e);
        setLoadError(message(e, 'Could not load your plan.'));
      });
  }, [navigate]);

  // Back from Checkout or the portal: Stripe may be ahead of its webhook, so
  // read the plan from Stripe, say what happened, and drop the query.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const checkout = params.get('checkout');
    const portal = params.get('portal');
    load(checkout === 'done' || portal === 'done').then(() => {
      if (checkout === 'done') enqueueSnackbar('Your plan is set up. Thank you!', {variant: 'success'});
      if (checkout === 'canceled') enqueueSnackbar('Nothing was charged.', {variant: 'info'});
    });
    if (checkout || portal) navigate('/settings/plan', {replace: true});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const goToStripe = async (call: () => Promise<{url: string}>) => {
    setBusy(true);
    try {
      const {url} = await call();
      window.location.assign(url);
    } catch (e) {
      logError('Error opening Stripe', {}, e as Error);
      enqueueSnackbar(message(e, 'Could not open Stripe.'), {variant: 'error', autoHideDuration: 8000});
      setBusy(false);
    }
  };

  const onChoose = async (pick: PlanPick) => {
    if (!data) return;
    if (!data.plan) {
      await goToStripe(() => startCheckout(navigate, toChoice(pick)));
      return;
    }
    setBusy(true);
    try {
      setData(await changePlan(navigate, toChoice(pick)));
      setChoosing(false);
      enqueueSnackbar('Your plan is changed.', {variant: 'success'});
    } catch (e) {
      logError('Error changing the plan', {}, e as Error);
      enqueueSnackbar(message(e, 'Could not change the plan.'), {variant: 'error', autoHideDuration: 10000});
    } finally {
      setBusy(false);
    }
  };

  const onAdd = async (n: number) => {
    setBusy(true);
    try {
      setData(await addEstimates(navigate, n));
      setAddOpen(false);
      enqueueSnackbar(`${n} estimate${n === 1 ? '' : 's'} added.`, {variant: 'success'});
    } catch (e) {
      logError('Error adding estimates', {}, e as Error);
      enqueueSnackbar(message(e, 'Could not add estimates.'), {variant: 'error', autoHideDuration: 10000});
    } finally {
      setBusy(false);
    }
  };

  const openChooser = () => {
    setChoosing(true);
    setTimeout(() => chooserRef.current?.scrollIntoView({behavior: 'smooth', block: 'start'}), 50);
  };

  if (forbidden) {
    return (
      <Paper sx={{p: 3}}>
        <Typography color="text.secondary" textAlign="center">The plan is the owner's to manage.</Typography>
      </Paper>
    );
  }
  if (loadError) {
    return (
      <Paper sx={{p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2}}>
        <Alert severity="error">{loadError}</Alert>
        <Button variant="outlined" onClick={() => load(false)}>Try again</Button>
      </Paper>
    );
  }
  if (!data) return <CenteredCircularSpinner/>;

  const live = !!data.plan;
  const canBuy = data.available;
  const hasCustomer = live || data.charges.length > 0 || !!data.paymentMethod;

  return (
    <Paper sx={{width: '100%', minHeight: '100%', boxSizing: 'border-box', p: {xs: 2, sm: 3}}}>
      <Box sx={{maxWidth: 640, mx: 'auto', display: 'flex', flexDirection: 'column', gap: 3}}>
        {data.problem && <Alert severity="warning">{data.problem}</Alert>}

        <Section title="Your plan">
          <CurrentPlan
            data={data}
            onChange={openChooser}
            onAdd={() => setAddOpen(true)}
            disabled={busy || !canBuy}
          />
        </Section>

        {(choosing || !live) && (
          <div ref={chooserRef}>
            <Section title={live ? 'Change your plan' : 'Choose your plan'}>
              <PlanChooser data={data} busy={busy} disabled={!canBuy} onChoose={onChoose}
                           onCancel={live ? () => setChoosing(false) : undefined}/>
            </Section>
          </div>
        )}

        <Section title="Payment method">
          <Card variant="outlined" sx={{p: 2, display: 'flex', alignItems: 'center', gap: 2}}>
            <Box sx={{flex: 1}}>
              <Typography>{data.paymentMethod?.label ?? 'No payment method yet'}</Typography>
              <Typography variant="body2" color="text.secondary">
                {data.paymentMethod?.sub ?? (hasCustomer ? 'Add one in Stripe' : 'Added when you choose your first plan')}
              </Typography>
            </Box>
            {hasCustomer && (
              <Button variant="outlined" disabled={busy || !canBuy} onClick={() => goToStripe(() => openBillingPortal(navigate))}>
                {data.paymentMethod ? 'Update' : 'Add'}
              </Button>
            )}
          </Card>
        </Section>

        <Section title="Billing history">
          <Card variant="outlined">
            {data.charges.length === 0 && (
              <Typography color="text.secondary" sx={{p: 2}}>No charges yet</Typography>
            )}
            {data.charges.map((charge, i) => <ChargeRow key={charge.id} charge={charge} first={i === 0}/>)}
          </Card>
          {hasCustomer && (
            <Box sx={{display: 'flex', justifyContent: 'center', mt: 1}}>
              <Button variant="text" disabled={busy || !canBuy} onClick={() => goToStripe(() => openBillingPortal(navigate))}>
                Receipts, payment method and cancelling (Stripe)
              </Button>
            </Box>
          )}
        </Section>
      </Box>

      {addOpen && data.plan && (
        <AddEstimatesDialog singleRate={data.catalog.singleRate} busy={busy} onCancel={() => setAddOpen(false)} onAdd={onAdd}/>
      )}
    </Paper>
  );
};

export default PlanPage;

function Section({title, children}: {title: string; children: React.ReactNode}) {
  return (
    <Box>
      <Typography variant="overline" color="text.secondary" component="h2" sx={{fontWeight: 700}}>{title}</Typography>
      {children}
    </Box>
  );
}

function CurrentPlan({data, onChange, onAdd, disabled}: {
  data: CompanyPlanDto; onChange: () => void; onAdd: () => void; disabled: boolean;
}) {
  const {plan, usage, catalog} = data;
  if (!plan) {
    return (
      <Card variant="outlined" sx={{p: 2}}>
        <Typography variant="h6">No plan yet</Typography>
        <Typography variant="body2" color="text.secondary">
          {count(usage.used)} estimate{usage.used === 1 ? '' : 's'} made this month. Choose a plan below to keep going.
        </Typography>
      </Card>
    );
  }

  const annual = plan.cycle === 'annual';
  const title = `${count(allowance(plan.perMonth, annual))} estimates ${annual ? 'a year' : 'a month'}`;
  const price = planPrice(catalog, plan.perMonth, annual);
  const allowed = usage.allowance ?? 0;
  const pct = allowed > 0 ? Math.min(100, (usage.used / allowed) * 100) : 0;
  const left = allowed - usage.used;
  const low = pct >= catalog.lowPoolAt * 100;
  const unit = annual ? 'for the year' : 'this month';
  const failing = plan.status === 'past_due' || plan.status === 'unpaid';

  return (
    <Card variant="outlined" sx={{p: 2, display: 'flex', flexDirection: 'column', gap: 1.5}}>
      <Box sx={{display: 'flex', alignItems: 'flex-start', gap: 2}}>
        <Box sx={{flex: 1}}>
          <Typography variant="h6">{title}</Typography>
          <Typography variant="body2" color="text.secondary">
            {plan.cancelAtPeriodEnd ? `Ends ${renewsOn(plan)}` : `Renews ${renewsOn(plan)}`}
          </Typography>
        </Box>
        <Typography variant="h6">{planMoney(price)} <Typography component="span" color="text.secondary">/ {annual ? 'yr' : 'mo'}</Typography></Typography>
      </Box>

      {failing && (
        <Alert severity="error">
          The last payment did not go through. Update the payment method, and Stripe will try again.
        </Alert>
      )}

      <Box>
        <LinearProgress
          variant="determinate"
          value={pct}
          aria-label="Estimates used"
          sx={{height: 10, borderRadius: 5, bgcolor: HAIR, '& .MuiLinearProgress-bar': {bgcolor: low ? AMBER : GREEN}}}
        />
        <Box sx={{display: 'flex', justifyContent: 'space-between', mt: 0.5}}>
          <Typography variant="body2">{count(usage.used)} of {count(allowed)} used {unit}</Typography>
          <Typography variant="body2" color={left < 0 ? 'error' : 'text.secondary'}>
            {left >= 0 ? `${count(left)} left` : `${count(-left)} over`}
          </Typography>
        </Box>
      </Box>

      {usage.extra > 0 && (
        <Typography variant="body2" color="text.secondary">
          {count(usage.extra)} estimate{usage.extra === 1 ? '' : 's'} added this period ({planMoney(usage.extra * catalog.singleRate)}).
        </Typography>
      )}
      {usage.adjustment > 0 && (
        <Typography variant="body2" color="text.secondary">
          The smaller plan starts on {renewsOn(plan)}; until then you keep the {count(usage.adjustment)} more you paid for.
        </Typography>
      )}
      {low && (
        <Alert severity={left < 0 ? 'error' : 'warning'}>
          {left < 0
            ? `You are ${count(-left)} past this plan ${unit}. Move up a tier or add estimates at ${planMoney(catalog.singleRate)} each.`
            : `You are running low: ${count(left)} left ${unit}. Moving up is almost always cheaper than adding them one at a time.`}
        </Alert>
      )}

      <Box sx={{display: 'flex', gap: 1, flexWrap: 'wrap'}}>
        <Button variant="contained" disabled={disabled} onClick={onChange} sx={{bgcolor: GREEN}}>Change plan</Button>
        <Button variant="outlined" disabled={disabled || failing} onClick={onAdd}>Add estimates</Button>
      </Box>
    </Card>
  );
}

function PlanChooser({data, busy, disabled, onChoose, onCancel}: {
  data: CompanyPlanDto; busy: boolean; disabled: boolean; onChoose: (pick: PlanPick) => void; onCancel?: () => void;
}) {
  const {catalog, plan} = data;
  const [cycle, setCycle] = useState<BillingCycle>(plan?.cycle ?? 'annual');
  const [pick, setPick] = useState<PlanPick | null>(null);

  const annual = cycle === 'annual';
  const ceiling = annual ? topTier(catalog) * 12 : topTier(catalog);
  const big = pick?.total != null;
  const change = describeChange(catalog, plan, pick);
  const floorTotal = (() => {
    // Mid-year the pool can only grow: the stepper stops at the plan's own.
    const first = firstBigTotal(catalog, cycle);
    if (plan && plan.cycle === 'annual' && annual) return Math.max(first, allowance(plan.perMonth, true));
    return first;
  })();

  const switchCycle = (next: BillingCycle | null) => {
    if (!next || next === cycle) return;
    setCycle(next);
    setPick(null);
  };

  const off = Math.round(catalog.annualDiscount * 100);
  const flatEach = annual ? catalog.flatRate * (1 - catalog.annualDiscount) : catalog.flatRate;

  return (
    <Box sx={{display: 'flex', flexDirection: 'column', gap: 1}}>
      <ToggleButtonGroup exclusive fullWidth value={cycle} onChange={(_, v) => switchCycle(v)} size="small" color="success">
        <ToggleButton value="monthly">Monthly</ToggleButton>
        <ToggleButton value="annual">Yearly · save {off}%</ToggleButton>
      </ToggleButtonGroup>

      <Box sx={{bgcolor: GREEN_SOFT, border: `1px solid ${GREEN_EDGE}`, borderRadius: 2, p: 1.5, my: 1}}>
        <Typography fontWeight={700} color={GREEN}>Everything is included</Typography>
        <Typography variant="body2">
          The only thing you are choosing here is how many estimates you need. Every feature is included on every plan,
          and you can add as many team members as you want at no extra charge.
        </Typography>
      </Box>

      {catalog.tiers.map(t => {
        const n = t.estimatesPerMonth;
        const tierPick: PlanPick = {cycle, tier: n};
        const on = pick?.tier === n;
        const locked = isLocked(plan, tierPick);
        const current = plan && plan.cycle === cycle && plan.perMonth === n;
        const price = annual ? annualFor(catalog, n) : priceFor(catalog, n);
        const each = annual ? price / 12 / n : priceFor(catalog, n) / n;
        return (
          <TierRow
            key={n}
            on={on}
            dimmed={locked}
            title={annual ? `${count(n * 12)} estimates a year` : `${n} estimates a month`}
            tag={current ? 'current' : locked ? 'at renewal' : undefined}
            sub={`${rateMoney(each)} an estimate`}
            price={planMoney(price)}
            per={annual ? '/yr' : '/mo'}
            onClick={() => setPick(on ? null : tierPick)}
          />
        );
      })}

      <TierRow
        on={big}
        dimmed={!!plan && plan.cycle === 'annual' && !annual}
        title={`More than ${count(ceiling)} ${annual ? 'a year' : 'a month'}`}
        sub={`${rateMoney(flatEach)} an estimate, however many you need`}
        onClick={() => setPick(big ? null : {cycle, total: floorTotal})}
      />
      {big && pick?.total != null && (
        <Box sx={{bgcolor: GREEN_SOFT, borderRadius: 2, p: 2}}>
          <Box sx={{display: 'flex', alignItems: 'center', gap: 2}}>
            <IconButton aria-label="Fewer" disabled={pick.total - catalog.bigStep < floorTotal}
                        onClick={() => setPick({cycle, total: pick.total! - catalog.bigStep})} sx={{border: `1px solid ${HAIR}`}}>−</IconButton>
            <Box sx={{flex: 1, textAlign: 'center'}}>
              <Typography variant="h4" fontWeight={700}>{count(pick.total)}</Typography>
              <Typography variant="body2" color="text.secondary">{annual ? 'a year' : 'a month'}</Typography>
            </Box>
            <IconButton aria-label="More" onClick={() => setPick({cycle, total: pick.total! + catalog.bigStep})}
                        sx={{border: `1px solid ${HAIR}`}}>+</IconButton>
          </Box>
          <Typography variant="h5" color={GREEN} fontWeight={700} sx={{mt: 1}}>
            {planMoney(pickPrice(catalog, pick))} <Typography component="span" color="text.secondary">{annual ? 'per year' : 'per month'}</Typography>
          </Typography>
          <Typography variant="body2" color="text.secondary">Goes up {catalog.bigStep} at a time.</Typography>
        </Box>
      )}

      {change.kind === 'change' && change.added != null && change.added > 0 && (
        <Box sx={{bgcolor: GREEN_SOFT, borderRadius: 2, p: 2}}>
          <Box sx={{display: 'flex', alignItems: 'center'}}>
            <Typography fontWeight={700} color={GREEN} sx={{flex: 1}}>Adding now</Typography>
            <Typography variant="h6" color={GREEN} fontWeight={700}>{planMoney(change.cost ?? 0)}</Typography>
          </Box>
        </Box>
      )}
      {(change.kind === 'change' || change.kind === 'checkout') && (
        <Typography variant="body2" color="text.secondary">{change.note}</Typography>
      )}
      {change.kind === 'locked' && <Alert severity="info">{change.note}</Alert>}

      <Typography variant="body2" color="text.secondary" sx={{mt: 1}}>
        {annual
          ? `A yearly plan is a pool for the whole year, so you can use them as fast as the season needs. If you run out ` +
            `before the year is up you can move up to a bigger plan, or add estimates one at a time for ${planMoney(catalog.singleRate)} each.`
          : `If you use up your estimates before the month is out you can move up to a bigger plan, or add estimates one at ` +
            `a time for ${planMoney(catalog.singleRate)} each. Moving up is almost always the cheaper of the two.`}
      </Typography>

      <Box sx={{display: 'flex', gap: 1, mt: 1}}>
        {onCancel && <Button variant="outlined" onClick={onCancel} disabled={busy}>Cancel</Button>}
        <Button
          variant="contained"
          sx={{flex: 1, bgcolor: GREEN}}
          disabled={disabled || busy || (change.kind !== 'change' && change.kind !== 'checkout')}
          onClick={() => pick && onChoose(pick)}
          startIcon={busy ? <CircularProgress size={16} color="inherit"/> : undefined}
        >
          {change.kind === 'change' || change.kind === 'checkout' || change.kind === 'same' ? change.label : 'Choose a plan'}
        </Button>
      </Box>
    </Box>
  );
}

function TierRow({on, dimmed, title, tag, sub, price, per, onClick}: {
  on: boolean; dimmed: boolean; title: string; tag?: string; sub: string; price?: string; per?: string; onClick: () => void;
}) {
  return (
    <ButtonBase
      onClick={onClick}
      role="radio"
      aria-checked={on}
      sx={{
        display: 'flex', alignItems: 'center', gap: 1.5, textAlign: 'left', width: '100%',
        p: 1.5, borderRadius: 2, opacity: dimmed ? 0.45 : 1,
        border: `1.5px solid ${on ? GREEN : HAIR}`, bgcolor: on ? GREEN_SOFT : 'background.paper',
      }}
    >
      <Box sx={{
        width: 18, height: 18, borderRadius: '50%', border: `2px solid ${on ? GREEN : '#9e9e9e'}`, flexShrink: 0,
        boxShadow: on ? `inset 0 0 0 3px #fff` : undefined, bgcolor: on ? GREEN : 'transparent',
      }}/>
      <Box sx={{flex: 1}}>
        <Typography>
          {title}{tag && <Typography component="span" variant="body2" color="text.secondary"> · {tag}</Typography>}
        </Typography>
        <Typography variant="body2" color="text.secondary">{sub}</Typography>
      </Box>
      {price && (
        <Typography fontWeight={700}>{price} <Typography component="span" variant="body2" color="text.secondary">{per}</Typography></Typography>
      )}
    </ButtonBase>
  );
}

const chargeStatus: Record<CompanyPlanChargeDto['status'], string> = {paid: 'Paid', due: 'Due', failed: 'Failed', void: 'Void'};

function ChargeRow({charge, first}: {charge: CompanyPlanChargeDto; first: boolean}) {
  const date = new Date(charge.date).toLocaleDateString('en-US', {month: 'short', day: 'numeric', year: 'numeric'});
  const body = (
    <Box sx={{display: 'flex', gap: 2, alignItems: 'center', p: 1.5, borderTop: first ? 'none' : `1px solid ${HAIR}`}}>
      <Box sx={{flex: 1}}>
        <Typography>{date}</Typography>
        {charge.lines[0] && (
          <Typography variant="body2" color="text.secondary" noWrap>{charge.lines[0].description}</Typography>
        )}
      </Box>
      <Typography>{planMoney(charge.amountCents / 100)}</Typography>
      <Typography variant="body2" color={charge.status === 'failed' ? 'error' : charge.status === 'paid' ? GREEN : 'text.secondary'}>
        {chargeStatus[charge.status]}
      </Typography>
    </Box>
  );
  return charge.receiptUrl
    ? <Link href={charge.receiptUrl} target="_blank" rel="noopener noreferrer" underline="none" color="inherit"
            aria-label={`Receipt for ${date}`}>{body}</Link>
    : body;
}

function AddEstimatesDialog({singleRate, busy, onCancel, onAdd}: {
  singleRate: number; busy: boolean; onCancel: () => void; onAdd: (n: number) => void;
}) {
  const [n, setN] = useState(1);
  return (
    <Dialog open onClose={busy ? undefined : onCancel} fullWidth maxWidth="xs">
      <DialogTitle>Add estimates</DialogTitle>
      <DialogContent>
        <Box sx={{display: 'flex', alignItems: 'center', gap: 2, my: 1}}>
          <IconButton aria-label="Fewer" disabled={n <= 1} onClick={() => setN(n - 1)} sx={{border: `1px solid ${HAIR}`}}>−</IconButton>
          <Box sx={{flex: 1, textAlign: 'center'}}>
            <Typography variant="h4" fontWeight={700}>{n}</Typography>
            <Typography variant="body2" color="text.secondary">{n === 1 ? 'extra estimate' : 'extra estimates'}</Typography>
          </Box>
          <IconButton aria-label="More" onClick={() => setN(n + 1)} sx={{border: `1px solid ${HAIR}`}}>+</IconButton>
        </Box>
        <Typography variant="h5" color={GREEN} fontWeight={700}>
          {planMoney(n * singleRate)} <Typography component="span" color="text.secondary">{planMoney(singleRate)} an estimate</Typography>
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{mt: 1}}>
          Charged now to the payment method on file. Moving up a tier costs less than 10 of these, so if you are going over
          most months it is worth changing your plan.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={busy}>Cancel</Button>
        <Button variant="contained" onClick={() => onAdd(n)} disabled={busy} sx={{bgcolor: GREEN}}
                startIcon={busy ? <CircularProgress size={16} color="inherit"/> : undefined}>
          Add {n} for {planMoney(n * singleRate)}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
